// TODO: remove if legacy lists deprecated
import { FieldComponentProps } from '@/components/forms/fields/types';
import { Badge } from '@/components/ui/Badge/badge';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import {
  Building2,
  LucideIcon,
  Megaphone,
  Plus,
  Tag as TagIcon,
  X,
} from 'lucide-react';
import React, { useMemo, useState } from 'react';

// Legacy `key:value` tag format (e.g. `department:climate`). Mirrors
// `tagOptions`/`templateTagOptions` in the legacy frontend
// (frontend/src/common/logic/list.ts). Will be removed when label-based
// tagging replaces this post-migration.

interface TagKeyOption {
  icon: LucideIcon;
  values: string[];
}
export type TagKeyOptions = Record<string, TagKeyOption>;

export const checklistTagKeys: TagKeyOptions = {
  label: { icon: TagIcon, values: ['partial', 'composed'] },
  department: {
    icon: Building2,
    values: [
      'climate',
      'electrical',
      'greenhouse',
      'irrigation',
      'service',
      'climate-cells',
    ],
  },
};

export const templateTagKeys: TagKeyOptions = {
  ...checklistTagKeys,
  feedback: { icon: Megaphone, values: ['true', 'false'] },
};

const TAG_RE = /^([\w-]+):(.*)$/;

// Bare legacy tags used by the old system to flag templates. They carry no
// user-facing meaning and clutter list/detail views, so we filter them out
// of rendering while preserving them in the underlying data.
const HIDDEN_LEGACY_TAGS = new Set(['qa', 'template']);

export function visibleTags(tags: string[]): string[] {
  return tags.filter((t) => !HIDDEN_LEGACY_TAGS.has(t));
}

function parseTag(tag: string) {
  const m = TAG_RE.exec(tag);
  return m ? { key: m[1], value: m[2] } : null;
}

// Department colors mirror the legacy ParameterizedSearch SCSS palette.
const departmentValueClasses: Record<string, string> = {
  climate: 'bg-red-600 text-white',
  'climate-cells': 'bg-red-600 text-white',
  greenhouse: 'bg-green-600 text-white',
  electrical: 'bg-yellow-500 text-black',
  irrigation: 'bg-blue-600 text-white',
  service: 'bg-gray-500 text-white',
};
const departmentKeyClasses: Record<string, string> = {
  climate: 'bg-red-200 text-red-900',
  'climate-cells': 'bg-red-200 text-red-900',
  greenhouse: 'bg-green-200 text-green-900',
  electrical: 'bg-yellow-200 text-yellow-900',
  irrigation: 'bg-blue-200 text-blue-900',
  service: 'bg-gray-200 text-gray-900',
};

export function TagBadge({
  tag,
  options = templateTagKeys,
  onRemove,
  compact = false,
}: {
  tag: string;
  options?: TagKeyOptions;
  onRemove?: () => void;
  /** When true, omit the key text and show only the icon + value. */
  compact?: boolean;
}) {
  const parsed = parseTag(tag);
  if (!parsed) {
    return (
      <Badge variant="outline" className="gap-1 text-xs">
        {tag}
        {onRemove && <RemoveBtn onClick={onRemove} />}
      </Badge>
    );
  }
  const Icon = options[parsed.key]?.icon ?? TagIcon;
  const isDepartment = parsed.key === 'department';
  const keyClass = isDepartment
    ? (departmentKeyClasses[parsed.value] ?? 'bg-muted text-muted-foreground')
    : 'bg-muted text-muted-foreground';
  const valueClass = isDepartment
    ? (departmentValueClasses[parsed.value] ?? 'bg-background')
    : 'bg-background';
  return (
    <Badge
      variant="outline"
      className="gap-0 overflow-hidden p-0 text-xs"
      title={compact ? `${parsed.key}: ${parsed.value}` : undefined}
    >
      <span
        className={cn(
          'flex shrink-0 items-center gap-1 self-stretch px-1.5 py-0.5 whitespace-nowrap',
          keyClass,
        )}
      >
        <Icon className="size-3 shrink-0" />
        {!compact && parsed.key}
      </span>
      <span
        className={cn(
          'flex min-w-0 items-center gap-1 self-stretch px-1.5 py-0.5 whitespace-nowrap',
          valueClass,
        )}
      >
        <span className="truncate">{parsed.value}</span>
        {onRemove && <RemoveBtn onClick={onRemove} />}
      </span>
    </Badge>
  );
}

function RemoveBtn({ onClick }: { onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
      className="hover:text-foreground rounded-full"
    >
      <X className="size-3" />
    </button>
  );
}

function TagPicker({
  options,
  existing,
  onAdd,
}: {
  options: TagKeyOptions;
  existing: string[];
  onAdd: (tag: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [selectedKey, setSelectedKey] = useState<string | null>(null);

  const close = () => {
    setOpen(false);
    setSelectedKey(null);
  };

  return (
    <Popover
      open={open}
      onOpenChange={(o) => {
        setOpen(o);
        if (!o) setSelectedKey(null);
      }}
    >
      <PopoverTrigger asChild>
        <Button type="button" variant="outline" size="sm" className="h-6 gap-1">
          <Plus className="size-3" />
          Add tag
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-56 p-0" align="start">
        <Command>
          {selectedKey === null ? (
            <CommandList>
              <CommandEmpty>No keys</CommandEmpty>
              <CommandGroup heading="Tag key">
                {Object.entries(options).map(([key, { icon: Icon }]) => (
                  <CommandItem
                    key={key}
                    value={key}
                    onSelect={() => setSelectedKey(key)}
                  >
                    <Icon className="size-3.5" />
                    {key}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          ) : (
            <CommandList>
              <CommandEmpty>No values</CommandEmpty>
              <CommandGroup heading={selectedKey}>
                {options[selectedKey].values.map((v) => {
                  const tag = `${selectedKey}:${v}`;
                  const taken = existing.includes(tag);
                  return (
                    <CommandItem
                      key={v}
                      value={v}
                      disabled={taken}
                      onSelect={() => {
                        onAdd(tag);
                        close();
                      }}
                    >
                      {v}
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            </CommandList>
          )}
        </Command>
      </PopoverContent>
    </Popover>
  );
}

function makeTagsField(options: TagKeyOptions): React.FC<FieldComponentProps> {
  return ({ formMode, controllerField, field }) => {
    const tags: string[] = useMemo(
      () => controllerField.value ?? [],
      [controllerField.value],
    );
    const shownTags = useMemo(() => visibleTags(tags), [tags]);

    if (formMode === 'loading') {
      return <Skeleton className="h-8 w-full" />;
    }

    const isEdit = formMode === 'edit' && !field.meta.readOnly;

    if (!isEdit) {
      if (shownTags.length === 0) {
        return <p className="text-muted-foreground">-</p>;
      }
      return (
        <div className="flex flex-wrap gap-1">
          {shownTags.map((t) => (
            <TagBadge key={t} tag={t} options={options} />
          ))}
        </div>
      );
    }

    const removeTag = (t: string) =>
      controllerField.onChange(tags.filter((x) => x !== t));
    const addTag = (t: string) => {
      if (tags.includes(t)) return;
      controllerField.onChange([...tags, t]);
    };

    return (
      <div
        className={cn(
          `border-input bg-background flex min-h-8 flex-wrap items-center gap-1 rounded-md
          border px-2 py-1`,
        )}
      >
        {shownTags.map((t) => (
          <TagBadge
            key={t}
            tag={t}
            options={options}
            onRemove={() => removeTag(t)}
          />
        ))}
        <TagPicker options={options} existing={tags} onAdd={addTag} />
      </div>
    );
  };
}

export const ChecklistTagsField = makeTagsField(checklistTagKeys);
export const TemplateTagsField = makeTagsField(templateTagKeys);
// Default name when rendering list tags polymorphically (checklist instance flavor).
export const ListTagsField = ChecklistTagsField;
